export interface CandleData {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export type Timeframe = '1m' | '5m' | '15m' | '30m' | '1h' | '4h' | '1d' | '1w' | '1M';

export interface ChartAdapter {
  fetchData(symbol: string, timeframe: Timeframe, isTestnet?: boolean, signal?: AbortSignal): Promise<CandleData[]>;
}

export class BinanceChartAdapter implements ChartAdapter {
  async fetchData(symbol: string, timeframe: Timeframe, isTestnet?: boolean, signal?: AbortSignal): Promise<CandleData[]> {
    const intervalMap: Record<Timeframe, string> = {
      '1m': '1m', '5m': '5m', '15m': '15m', '30m': '30m', '1h': '1h', '4h': '4h', '1d': '1d', '1w': '1w', '1M': '1M'
    };

    const formattedSymbol = symbol.toUpperCase().replace('-', '').replace('-SWAP', '');
    // isTestnet 값에 따라 엔드포인트 분기
    const base = isTestnet ? 'https://testnet.binancefuture.com' : 'https://fapi.binance.com';
    const url = `${base}/fapi/v1/klines?symbol=${formattedSymbol}&interval=${intervalMap[timeframe]}&limit=500&_t=${Date.now()}`;

    const response = await fetch(url, { signal, cache: 'no-store' });
    if (!response.ok) throw new Error(`Binance Fetch Error: ${response.statusText}`);

    const rawData = await response.json();
    if (!Array.isArray(rawData)) throw new Error('Invalid Binance data format');

    return rawData.map((d: any) => ({
      time: d[0] / 1000,
      open: parseFloat(d[1]),
      high: parseFloat(d[2]),
      low: parseFloat(d[3]),
      close: parseFloat(d[4]),
      volume: parseFloat(d[5]),
    }));
  }
}

export class OkxChartAdapter implements ChartAdapter {
  async fetchData(symbol: string, timeframe: Timeframe, isTestnet?: boolean, signal?: AbortSignal): Promise<CandleData[]> {
    const intervalMap: Record<Timeframe, string> = {
      '1m': '1m', '5m': '5m', '15m': '15m', '30m': '30m', '1h': '1H', '4h': '4H', '1d': '1D', '1w': '1W', '1M': '1M'
    };

    // OKX는 퍼블릭 캔들의 경우 실전/데모 구분 없이 동일한 엔드포인트를 주로 사용함
    const base = 'https://www.okx.com'; 
    // However, public candles are mostly the same. 
    const url = `${base}/api/v5/market/candles?instId=${symbol}&bar=${intervalMap[timeframe]}&limit=300&_t=${Date.now()}`;

    const response = await fetch(url, { signal, cache: 'no-store' });
    if (!response.ok) throw new Error(`OKX Fetch Error: ${response.statusText}`);

    const resData = await response.json();
    if (resData.code !== "0") throw new Error(resData.msg || 'OKX Data Error');

    const rawData = resData.data || [];
    return rawData.map((d: any) => ({
      time: parseInt(d[0]) / 1000,
      open: parseFloat(d[1]),
      high: parseFloat(d[2]),
      low: parseFloat(d[3]),
      close: parseFloat(d[4]),
      volume: parseFloat(d[5]),
    })).reverse();
  }
}

export class BybitChartAdapter implements ChartAdapter {
  async fetchData(symbol: string, timeframe: Timeframe, isTestnet?: boolean, signal?: AbortSignal): Promise<CandleData[]> {
    const intervalMap: Record<Timeframe, string> = {
      '1m': '1', '5m': '5', '15m': '15', '30m': '30', '1h': '60', '4h': '240', '1d': 'D', '1w': 'W', '1M': 'M'
    };

    // isTestnet 값에 따라 엔드포인트 분기
    const base = isTestnet ? 'https://api-testnet.bybit.com' : 'https://api.bybit.com';
    const url = `${base}/v5/market/kline?category=linear&symbol=${symbol.toUpperCase()}&interval=${intervalMap[timeframe]}&limit=200&_t=${Date.now()}`;

    const response = await fetch(url, { signal, cache: 'no-store' });
    if (!response.ok) throw new Error(`Bybit Fetch Error: ${response.statusText}`);

    const resData = await response.json();
    if (resData.retCode !== 0) throw new Error(resData.retMsg || 'Bybit Data Error');

    const rawData = resData.result.list || [];
    return rawData.map((d: any) => ({
      time: parseInt(d[0]) / 1000,
      open: parseFloat(d[1]),
      high: parseFloat(d[2]),
      low: parseFloat(d[3]),
      close: parseFloat(d[4]),
      volume: parseFloat(d[5]),
    })).reverse();
  }
}

export const getChartAdapter = (exchange?: string): ChartAdapter => {
  const ex = exchange?.toLowerCase();
  if (ex === 'okx') return new OkxChartAdapter();
  if (ex === 'bybit') return new BybitChartAdapter();
  if (ex === 'bitget') return new BinanceChartAdapter(); // Bitget uses similar structure or we can map to Binance for now
  return new BinanceChartAdapter();
};

